import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Modal.tsx
import { useEffect, useRef, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { Board } from '../../components/board';
import * as THREE from 'three';
import { formatLength } from "../../util";
const locale = {
    ru: { close: 'Закрыть' },
    en: { close: 'Close' },
    de: { close: 'Schließen' },
};
// ── Мини-превью одного элемента из группы ────────────────────────────────────
const PreviewCanvas = ({ data, elementIdx, cacheKey }) => {
    const el = data.elements[elementIdx];
    if (!el)
        return null;
    // считаем дистанцию камеры по размеру геометрии
    const verts = data.vertices[el.vertices];
    let maxSize = 0;
    for (let i = 0; i < verts.length; i++) {
        maxSize = Math.max(maxSize, Math.abs(verts[i]));
    }
    const dist = maxSize * 1.5;
    return (_jsxs(Canvas, { style: { width: '100%', height: '100%' }, camera: { position: [dist, dist * 0.5, dist], fov: 45 }, gl: { antialias: true }, children: [_jsx("ambientLight", { intensity: 0.6 }), _jsx("directionalLight", { position: [2, 4, 2], intensity: 1 }), _jsx("group", { rotation: data.rotation ? new THREE.Euler(...data.rotation) : undefined, children: _jsx(Board, { name: el.name, position: [0, 0, 0], rotation: el.rotation, material: data.material[el.material], vertices: data.vertices[el.vertices], uvs: data.uvs[el.uvs], indices: data.indices[el.indices] }, el.name) }), _jsx(OrbitControls, { enablePan: true })] }, cacheKey));
};
// ── Modal ─────────────────────────────────────────────────────────────────────
export const ModalPage = ({ open, onClose, title, children, groupDataList, spec, lang = 'en', units = 'ft' }) => {
    const t = locale[lang];
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const contentRef = useRef(null);
    const [activeSpecIdx, setActiveSpecIdx] = useState(0);
    const activeGroup = groupDataList?.find(r => r.specIds?.includes(spec?.[activeSpecIdx]?.specId ?? ''));
    const activeElementIdx = activeGroup?.elements.findIndex(el => el.specId === spec?.[activeSpecIdx]?.specId) ?? 0;
    console.log(groupDataList);
    console.log(activeGroup);
    console.log(activeSpecIdx);
    console.log(spec);
    // следим за resize
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    // свайп вниз для закрытия на мобиле
    const touchStartY = useRef(0);
    const handleTouchStart = (e) => {
        touchStartY.current = e.touches[0].clientY;
    };
    const handleTouchEnd = (e) => {
        const delta = e.changedTouches[0].clientY - touchStartY.current;
        if (delta > 80)
            onClose(); // свайп вниз на 80px — закрываем
    };
    useEffect(() => {
        if (open)
            setActiveSpecIdx(0);
    }, [open]);
    if (!open)
        return null;
    // ── Mobile ────────────────────────────────────────────────────────────────
    if (isMobile) {
        return (_jsxs(_Fragment, { children: [_jsx("div", { onClick: onClose, style: {
                        position: 'fixed', inset: 0,
                        background: 'rgba(0,0,0,0.5)',
                        zIndex: 999,
                    } }), _jsxs("div", { onTouchStart: handleTouchStart, onTouchEnd: handleTouchEnd, style: {
                        position: 'fixed', inset: 0,
                        background: '#fff',
                        zIndex: 1000,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }, children: [_jsx("div", { style: { display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }, children: _jsx("div", { style: { width: 36, height: 4, borderRadius: 2, background: '#cbd5e0' } }) }), activeGroup && (_jsx("div", { style: { height: '40%', flexShrink: 0, background: '#f7fafc' }, children: _jsx(PreviewCanvas, { data: activeGroup, elementIdx: activeElementIdx, cacheKey: spec?.[activeSpecIdx]?.specId }) })), spec && spec.length > 1 && (_jsx("div", { style: { padding: '8px 16px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }, children: _jsx("select", { value: activeSpecIdx, onChange: e => setActiveSpecIdx(Number(e.target.value)), style: {
                                    width: '100%',
                                    padding: '8px 12px',
                                    borderRadius: 8,
                                    border: '1.5px solid #e2e8f0',
                                    fontSize: 13,
                                    color: '#1a202c',
                                    background: '#f7fafc',
                                    cursor: 'pointer',
                                    appearance: 'none',
                                    outline: 'none',
                                }, children: spec.map((item, idx) => (_jsxs("option", { value: idx, children: [item.name, " \u2014 ", formatLength(item.l, units)] }, idx))) }) })), _jsxs("div", { style: {
                                display: 'flex', justifyContent: 'space-between',
                                alignItems: 'center', padding: '12px 16px',
                                borderBottom: '1px solid #e2e8f0', flexShrink: 0,
                            }, children: [_jsx("span", { style: { fontWeight: 600, fontSize: 15 }, children: title }), _jsx("button", { onClick: onClose, style: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#718096' }, children: "\u2715" })] }), _jsx("div", { ref: contentRef, style: { flex: 1, overflowY: 'auto', padding: 16 }, children: children })] })] }));
    }
    // ── Desktop ───────────────────────────────────────────────────────────────
    return (_jsxs(_Fragment, { children: [_jsx("div", { onClick: onClose, style: {
                    position: 'fixed', inset: 0,
                    background: 'rgba(0,0,0,0.2)',
                    zIndex: 999,
                } }), _jsxs("div", { style: {
                    position: 'fixed', left: 0, top: 0, bottom: 0,
                    width: '320px',
                    background: '#fff',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '8px 0 32px rgba(0,0,0,0.15)',
                    overflow: 'hidden',
                }, children: [_jsxs("div", { style: {
                            display: 'flex', justifyContent: 'space-between',
                            alignItems: 'center', padding: '16px 20px',
                            borderBottom: '1px solid #e2e8f0', flexShrink: 0,
                        }, children: [_jsx("span", { style: { fontWeight: 600, fontSize: 15 }, children: title }), _jsx("button", { onClick: onClose, style: {
                                    background: 'none', border: 'none',
                                    cursor: 'pointer', fontSize: 20, color: '#718096'
                                }, children: "\u2715" })] }), _jsx("div", { style: { flex: 1, overflowY: 'auto', padding: 20 }, children: children })] }), (activeGroup || spec) && (_jsxs("div", { style: {
                    position: 'fixed', left: '320px', top: 0, bottom: 0, // ← рядом с левой панелью
                    width: '420px',
                    background: '#fff',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '8px 0 32px rgba(0,0,0,0.15)',
                    overflow: 'hidden',
                    borderLeft: '1px solid #e2e8f0',
                }, children: [activeGroup && (_jsx("div", { style: { height: '40%', flexShrink: 0, background: '#f7fafc' }, children: _jsx(PreviewCanvas, { data: activeGroup, elementIdx: activeElementIdx, cacheKey: spec?.[activeSpecIdx]?.specId }) })), spec && spec.length > 0 && (_jsxs("div", { style: { flex: 1, overflowY: 'auto', padding: 16 }, children: [_jsx("div", { style: {
                                    fontSize: 11, fontWeight: 700, color: '#a0aec0',
                                    textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12
                                }, children: "Specification" }), spec.map((item, idx) => (_jsxs("div", { onClick: () => setActiveSpecIdx(idx), style: {
                                    padding: '10px 0',
                                    borderBottom: '1px solid #f0f0f0',
                                    fontSize: 13,
                                    cursor: 'pointer',
                                    background: activeSpecIdx === idx ? '#ebf4ff' : 'transparent',
                                    borderRadius: 6,
                                    paddingLeft: activeSpecIdx === idx ? 8 : 0,
                                    transition: 'all 0.15s',
                                }, children: [_jsx("div", { style: { fontWeight: 600, color: activeSpecIdx === idx ? '#4488ff' : '#1a202c' }, children: item.name }), _jsxs("div", { style: { color: '#718096', fontSize: 12 }, children: [formatLength(item.w, units), " \u00D7 ", formatLength(item.h, units), "\u00A0\u00B7\u00A0L: ", formatLength(item.l, units), "\u00A0\u00B7\u00A0", item.count, " pcs"] })] }, idx)))] }))] }))] }));
};
